/**
 * NC-1098 (Feat 2.0): build a folder tree from the flat training-file list.
 *
 * The training-data v2 model (NC-1095) carries:
 *   - `relativePath` — POSIX folder under C:\Schulungsunterlagen. For a file
 *     it is the *containing* folder; for a `kind:'folder'` node it is the
 *     folder itself. Missing/empty = root.
 *   - `kind` — 'folder' = an empty structure node (no blob); 'file' (default).
 *
 * The tree is derived purely from `relativePath` + `kind`, so folder segments
 * that only exist implicitly (a file at `Kapitel1/uebung.pdf` with no explicit
 * folder doc) still materialize as folders.
 */

import { TrainingFileMetadata } from '../../services/types';

export interface TrainingFileTreeNode {
  /** Last path segment; '' for the (virtual) root. */
  name: string;
  /** Full POSIX path from the root; '' for the root. */
  path: string;
  /** Child folders, keyed by segment name, sorted at read time. */
  children: Map<string, TrainingFileTreeNode>;
  /** Files that live directly in this folder. */
  files: TrainingFileMetadata[];
  /** The explicit `kind:'folder'` doc that created this node, if any. */
  folderDoc?: TrainingFileMetadata;
}

/**
 * Normalize a raw relativePath into clean POSIX segments. Tolerant of
 * leading/trailing/duplicate slashes and backslashes; drops `.`/`..` and empty
 * segments so a malformed path never escapes the tree (the BE is the real
 * validator — this is display hygiene only).
 */
export function normalizeRelativePath(raw?: string): string {
  if (!raw) return '';
  return raw
    .replace(/\\/g, '/')
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s !== '.' && s !== '..')
    .join('/');
}

function createNode(name: string, path: string): TrainingFileTreeNode {
  return { name, path, children: new Map(), files: [] };
}

/**
 * Build the folder tree. Returns a virtual root node whose `children`/`files`
 * are the top-level entries. Callers typically render `root.children` +
 * `root.files`.
 */
export function buildTrainingFileTree(
  files: TrainingFileMetadata[]
): TrainingFileTreeNode {
  const root = createNode('', '');

  const ensureDir = (segments: string[]): TrainingFileTreeNode => {
    let node = root;
    let acc = '';
    for (const seg of segments) {
      acc = acc ? `${acc}/${seg}` : seg;
      let child = node.children.get(seg);
      if (!child) {
        child = createNode(seg, acc);
        node.children.set(seg, child);
      }
      node = child;
    }
    return node;
  };

  for (const file of files) {
    const rel = normalizeRelativePath(file.relativePath);
    const segments = rel ? rel.split('/') : [];
    if (file.kind === 'folder') {
      const node = ensureDir(segments);
      node.folderDoc = file;
    } else {
      const node = ensureDir(segments);
      node.files.push(file);
    }
  }

  return root;
}

/** Child folders of a node, sorted alphabetically (case-insensitive). */
export function sortedChildFolders(
  node: TrainingFileTreeNode
): TrainingFileTreeNode[] {
  return Array.from(node.children.values()).sort((a, b) =>
    a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })
  );
}

/** Files of a node, sorted alphabetically by fileName (case-insensitive). */
export function sortedFiles(node: TrainingFileTreeNode): TrainingFileMetadata[] {
  return [...node.files].sort((a, b) =>
    a.fileName.localeCompare(b.fileName, 'de', { sensitivity: 'base' })
  );
}

/** True when a folder has no child folders and no files (safe to delete). */
export function isEmptyFolder(node: TrainingFileTreeNode): boolean {
  return node.children.size === 0 && node.files.length === 0;
}
